import { CompartmentSystem, HgsArticle } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, effect, input, signal } from '@angular/core';
import { HgsSvgImageComponent } from './components/hgs-svg-image.component';
import { getImageProxyUrl } from './hgs-helper';
import { TranslocoPipe } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';

type SystemComponent = {
  componentId: string;
  genericArticleNos?: number[];
  group?: { language: string; title: string };
  name?: { language: string; title: string };
  placementText?: { language: string; title: string };
  shortName?: string;
  showInComponentList: boolean;
  xAbs: string;
  yAbs: string;
};

export type SystemImage = {
  components?: SystemComponent[];
  imagePlacementUrl?: string;
  systemId: string;
  systemName?: { language: string; title: string };
};

@Component({
  selector: 'app-hgs-location',
  template: `
    @if (data() && data()?.text) {
      <div class="pb-24">
        @for (image of images(); track $index) {
          <div class="mb-6">
            <h2>{{ image.systemName?.title }}</h2>

            <div class="mt-4 flex w-full flex-col gap-2 lg:flex-row">
              <app-hgs-svg-image
                [system]="image"
                [highlightedComponentId]="selectedComponentId()"
                (onHover)="handleHover($event)"
                class="block w-full lg:w-2/3"
                imgClass="" />

              <div class="w-full lg:w-1/3">
                @for (comp of image.components; track comp.componentId) {
                  <button
                    (mouseenter)="onHoverComponent(comp.componentId)"
                    (mouseleave)="onHoverComponent(null)"
                    [attr.class]="
                      'text-left mb-4 inline-block rounded-lg border border-gray-300 bg-blue-100 p-2 text-sm' +
                      (selectedComponentId() === comp.componentId ? ' outline-blue-500 outline outline-2' : '')
                    ">
                    <h3 class="text-sm font-bold">{{ comp.componentId }} - {{ comp.name?.title }} ({{ comp.shortName }})</h3>

                    @if (comp.group?.title) {
                      <p class="mt-2 text-sm">
                        <strong>{{ 'documents.hgs.group' | transloco }}:</strong>
                        {{ comp.group?.title }}
                      </p>
                    }

                    @if (comp.placementText?.title) {
                      <div class="mt-2 text-sm">
                        <p>
                          <strong>{{ 'documents.hgs.location' | transloco }}:</strong>
                          {{ comp.placementText?.title }}
                        </p>
                        @if (!comp.xAbs || !comp.yAbs) {
                          <p class="mt-1 italic">({{ 'documents.hgs.notDisplayable' | transloco }})</p>
                        }
                      </div>
                    }
                  </button>
                }
              </div>
            </div>
          </div>
        }
      </div>
    } @else {
      <p class="mt-8 text-center text-sm text-gray-500">{{ 'global.noOEDataFound' | transloco }}</p>
    }
  `,
  imports: [CommonModule, TranslocoPipe, MarkdownModule, HgsSvgImageComponent],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
})
export class HgsLocationComponent {
  data = input<HgsArticle | undefined>();

  images = signal<SystemImage[]>([]);
  selectedComponentId = signal<string | null>(null);

  onChange = effect(() => {
    const systems = this.data()?.data?.componentLocation?.engineCompartment?.systems;
    systems?.forEach((system: CompartmentSystem) => {
      if (system.imagePlacementUrl) {
        system.imagePlacementUrl = getImageProxyUrl(system.imagePlacementUrl);
        this.images.update((imgs) => [...imgs, system as SystemImage]);
      }
    });
  });

  onHoverComponent(componentId: string | null) {
    this.selectedComponentId.set(componentId);
  }

  handleHover(componentId: string | null) {
    this.selectedComponentId.set(componentId);
  }
}
